import React, { useState } from 'react';
import PageHero from '@/components/PageHero';
import SEO from '@/components/SEO';
import { Play, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

const VIDEOS = [
  {
    src: 'https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/u7te1rl6_FB_VID_4688784155383909507.mp4',
    title: 'A Message from Joseph M. Kress',
    caption: 'Real stories. Real impact. Prevention that reaches students where they are.',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/mkvi7c2x_FB_VID_9000623604544173174.mp4',
    title: 'Behind the Program',
    caption: 'A look at the mission behind Drug & Alcohol Awareness USA.',
  },
];

const POSTERS = [
  {
    src: 'https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/38d07zry_file_000000008c9c7206aeb02d43ec4a0893.png',
    title: 'Did You Know?',
    caption: 'One bad decision can have lasting consequences.',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/ip8jqhii_FB_IMG_1783978592764.jpg',
    title: 'Single Handed — Joseph Michael Kress',
    caption: 'He is just looking to stop the next overdose before it happens.',
  },
  {
    src: 'https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/yk3gbs8p_FB_IMG_1783978612595.jpg',
    title: 'Single Handed — The Book',
    caption: 'A front-row seat to the harsh realities of police work.',
  },
];

export default function Media() {
  const [active, setActive] = useState(null);

  return (
    <div>
      <SEO
        title="Media & Campaign"
        description="Watch featured videos and view awareness campaign posters from the Drug and Alcohol Awareness program by Joseph M. Kress."
        path="/media"
      />
      <PageHero
        title="Media & Campaign"
        subtitle="Videos, campaign posters, and the story behind the mission."
        image="https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=1920&q=80"
        crumbs={['Media']}
      />

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Videos */}
        <div className="mb-16">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Watch</div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">Featured Videos</h2>
            <div className="mt-3 h-1 w-16 bg-amber-400" />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {VIDEOS.map((v, i) => (
              <div key={i} className="group bg-black rounded-lg overflow-hidden shadow-sm border border-slate-200">
                <div className="relative aspect-video bg-black">
                  {active === i ? (
                    <video
                      src={v.src}
                      controls
                      autoPlay
                      className="w-full h-full object-contain bg-black"
                    />
                  ) : (
                    <button
                      onClick={() => setActive(i)}
                      className="absolute inset-0 flex items-center justify-center group/play"
                    >
                      <video
                        src={v.src}
                        muted
                        preload="metadata"
                        className="absolute inset-0 w-full h-full object-cover opacity-70"
                      />
                      <div className="relative z-10 h-20 w-20 rounded-full bg-amber-500 group-hover/play:bg-amber-400 flex items-center justify-center shadow-lg transition-all group-hover/play:scale-110">
                        <Play className="h-8 w-8 text-[#0b2545] ml-1" fill="currentColor" />
                      </div>
                    </button>
                  )}
                </div>
                <div className="p-5 bg-white">
                  <h3 className="font-serif text-lg font-bold text-[#0b2545]">{v.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{v.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Campaign Posters */}
        <div className="mb-16">
          <div className="mb-8">
            <div className="text-xs uppercase tracking-[0.3em] text-amber-600 font-semibold mb-2">Campaign</div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#0b2545]">Awareness Posters</h2>
            <div className="mt-3 h-1 w-16 bg-amber-400" />
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {POSTERS.map((p, i) => (
              <a
                key={i}
                href={p.src}
                target="_blank"
                rel="noopener noreferrer"
                className="group block bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200 hover:shadow-xl transition-all"
              >
                <div className="aspect-[3/4] overflow-hidden bg-slate-100">
                  <img
                    src={p.src}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <h3 className="font-serif font-bold text-[#0b2545]">{p.title}</h3>
                  <p className="text-sm text-slate-600 mt-1">{p.caption}</p>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Book CTA */}
        <div className="grid md:grid-cols-[280px_1fr] gap-8 items-center bg-gradient-to-br from-[#0b2545] to-[#123a6b] rounded-lg p-8 md:p-12 text-white">
          <img
            src="https://customer-assets.emergentagent.com/job_substance-education-4/artifacts/yk3gbs8p_FB_IMG_1783978612595.jpg"
            alt="Single Handed by Joseph M. Kress"
            className="w-full max-w-[260px] mx-auto rounded-md shadow-2xl"
          />
          <div>
            <div className="flex items-center gap-2 text-amber-300 text-xs uppercase tracking-[0.3em] font-semibold mb-3">
              <BookOpen className="h-4 w-4" /> The Book
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Single Handed</h2>
            <p className="text-slate-200 leading-relaxed mb-6">
              By Joseph M. Kress — one man’s war against the drug crisis. A gut-wrenching,
              unfiltered look at the harsh realities of police work and the fight to save the next
              life before it is lost.
            </p>
            <Button asChild className="bg-amber-500 hover:bg-amber-400 text-[#0b2545] font-semibold">
              <Link to="/bio">
                Learn about the Author <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
